/**
 * Map a caught dashboard-auth error to a user-facing view that DISTINGUISHES
 * "signed out" (no / invalid / missing session -> the right CTA is Sign in)
 * from "authenticated but not provisioned" (a valid identity with no staff
 * profile -> Sign in is the WRONG CTA; the fix is provisioning, not re-login).
 *
 * The distinction is driven by the backend's structured reason codes
 * (lib/errors.ts DASHBOARD_AUTH_REASON), surfaced as PortalApiError.code. Before
 * these codes every 401 showed one misleading "Sign in to continue" card even
 * for a signed-in owner whose account was mis-provisioned -- the exact confusion
 * in the 2026-05-15 and 2026-07-21 incidents.
 */
import { PortalApiError } from '@/lib/portal-api';

export type DashboardAuthErrorKind = 'signed_out' | 'unresolved' | 'error';

export interface DashboardAuthErrorView {
  readonly kind: DashboardAuthErrorKind;
  readonly title: string;
  readonly body: string;
  /** true when the correct CTA is "sign in" (a session problem). false when the
   *  user IS authenticated but access is not provisioned / is throttled. */
  readonly showSignIn: boolean;
}

export function dashboardAuthErrorView(err: unknown): DashboardAuthErrorView {
  const code = err instanceof PortalApiError ? err.code : null;
  const status = err instanceof PortalApiError ? err.status : null;

  // Authenticated, but the identity has no staff profile / role yet.
  if (code === 'NO_PROFILE') {
    return {
      kind: 'unresolved',
      title: 'Account not set up for the dashboard',
      body:
        'You are signed in, but this account does not have dashboard access yet. ' +
        'Ask your administrator to provision your staff role, then reload.',
      showSignIn: false,
    };
  }

  // Too many failed attempts from this network -- re-login will not help now.
  if (code === 'RATE_LIMITED') {
    return {
      kind: 'error',
      title: 'Too many attempts',
      body:
        'Too many failed sign-in attempts from your network. ' +
        'Wait a few minutes, then reload.',
      showSignIn: false,
    };
  }

  // No token / invalid token, or any other 401: a genuine session problem.
  if (code === 'NO_TOKEN' || code === 'TOKEN_INVALID' || status === 401) {
    return {
      kind: 'signed_out',
      title: 'Sign in to continue',
      body: "We couldn't load your dashboard access. Sign in again to retry.",
      showSignIn: true,
    };
  }

  // Non-auth failure (network / 5xx / unknown).
  return {
    kind: 'error',
    title: 'Something went wrong',
    body: "We couldn't load your dashboard access. Reload to retry.",
    showSignIn: true,
  };
}
